/**
 * InheritanceCard — hierarchical-mode only. Lists child TARAs that
 * fed the current parent: name, record count, relevance count + %,
 * status badge.
 *
 * Has a `shouldRender` predicate the parent grid checks before
 * mounting — the card is hidden entirely in individual mode.
 *
 * Pure presentation; consumes `data.inheritance`.
 */
import React from 'react';
import StatusPill from '../../../../components/ui/StatusPill/StatusPill';
import {
  TypographyH2,
  TypographyMuted,
  TypographySmall,
} from '../../../../components/ui/Typography';
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
} from '../../../../components/ui/Empty';
import type { AggregatedOverviewData } from '../../aggregate/types';
import type { StepStatus } from '../../../athena/state/workflowV2Types';
import styles from './InheritanceCard.module.css';

interface InheritanceCardProps {
  data: AggregatedOverviewData;
}

/** Grid uses this to decide whether to mount the card. */
export const shouldRender = (data: AggregatedOverviewData): boolean =>
  Array.isArray(data.inheritance);

const STATUS_TONE: Record<StepStatus, 'success' | 'warning' | 'info' | 'error' | 'neutral'> = {
  empty: 'neutral',
  generating: 'warning',
  draft: 'info',
  revising: 'warning',
  approved: 'success',
  failed: 'error',
};

const STATUS_LABEL: Record<StepStatus, string> = {
  empty: 'Empty',
  generating: 'Generating',
  draft: 'Draft',
  revising: 'In review',
  approved: 'Approved',
  failed: 'Failed',
};

export default function InheritanceCard({ data }: InheritanceCardProps) {
  const taras = data.inheritance ?? [];

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <TypographyH2 className={styles.cardTitle}>Inherited from Child TARAs</TypographyH2>
        <TypographyMuted className={styles.cardMeta}>
          {taras.length} child TARA{taras.length === 1 ? '' : 's'}
        </TypographyMuted>
      </div>
      {taras.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No child TARAs imported yet</EmptyTitle>
            <EmptyDescription>Populates once a child TARA is imported.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className={styles.list}>
          {taras.map((c) => {
            const pct = c.records > 0 ? Math.round((c.relevant / c.records) * 100) : 0;
            return (
              <div key={c.name} className={styles.row}>
                <span className={styles.dot} />
                <TypographySmall className={styles.name}>{c.name}</TypographySmall>
                <TypographySmall className={styles.stat}>
                  {c.records} records · {c.relevant} relevant ({pct}%)
                </TypographySmall>
                <StatusPill tone={STATUS_TONE[c.status]} label={STATUS_LABEL[c.status]} size="sm" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
